import {
  Alert,
  ArrowRightIcon,
  Box,
  Button,
  CheckCircle2Icon,
  Divider,
  Drawer,
  DrawerBody,
  Link,
  RefreshIcon,
  Text,
  UserIcon
} from '@razorpay/blade/components';
import { ModalActionButtons } from 'components/SalaryComponents/components/ModalActionButtons';
import {
  DetailsContainer,
  DetailsFooter,
  DetailsHeader,
  DetailsSection,
  DetailsSectionHeading,
  DetailsSectionItem,
  DetailsSectionItemsContainer,
  DetailsViewSkeleton,
} from 'components/SalaryComponents/components/details/DetailsViewHelpers';
import { useDetailsView, useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { ActionModals } from 'components/SalaryComponents/modals';
import { salaryComponentParams } from '../../constants';

import { NonPayableBenefitsDummyData } from 'components/SalaryComponents/__tests__/data';
import { NonPayableBenefitsComponentType } from 'components/SalaryComponents/types';
import React, { useEffect, useState } from 'react';
import { EmptyView } from '../../components/EmptyView';

export const NonPayableBenefitsDetailView = () => {
  const { paramId: salaryComponentId, closeDetailsView } = useDetailsView(
    salaryComponentParams.componentId,
  );
  const { openPredefinedEditPage, openNonPayableBenefitsEditPage } = useSalaryComponentHelpers();
  const [currentActiveModal, setCurrentActiveModal] = useState<string | null>(null);
  const isOpen = !!salaryComponentId;

  // Mock data state
  const [data, setData] = useState<NonPayableBenefitsComponentType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Temporarily using this to mock data
  useEffect(() => {
    if (salaryComponentId) {
      const foundComponent = NonPayableBenefitsDummyData.find(
        (component) => component.id === salaryComponentId,
      );

      setData(foundComponent || null);
      setIsError(!foundComponent);
    } else {
      setData(null);
    }
  }, [salaryComponentId]);

  const refetch = () => {
    setIsLoading(true);

    // Simulate API delay
    setTimeout(() => {
      const foundComponent = NonPayableBenefitsDummyData.find(
        (component) => component.id === salaryComponentId,
      );

      setData(foundComponent || null);
      setIsLoading(false);
      setIsError(!foundComponent);
    }, 500);
  };

  const openModal = (modal: string) => {
    setCurrentActiveModal(modal);
  };

  const closeModal = () => {
    setCurrentActiveModal(null);
  };

  /* Commented out actual API call for future use
  const { data, isLoading, refetch, isError } = useQuery({
    queryKey: [salaryComponentsQueries.getSalaryComponentDetailsQuery],
    queryFn: () =>
      api.salaryComponents.queries.getSalaryComponent<NonPayableBenefitsComponentType>({
        id: salaryComponentId!,
        category: 'NON_PAYABLE_BENEFITS_CATEGORY',
      }),
    enabled: isOpen,
    select(data) {
      return data.payrollGetSalaryComponent.data.salaryComponent;
    },
    onError(error) {
      console.log(error);
    },
    cacheTime: 0,
  });
  */

  const onDismiss = () => {
    // Commenting out API invalidation since we're using mock data
    /* 
    queryClient.invalidateQueries({
      queryKey: [salaryComponentsQueries.getSalaryComponentDetailsQuery, salaryComponentId],
    });
    */
    closeModal();
    closeDetailsView();
  };

  const { nonPayableBenefitsDetails } = data?.settings || {};

  const handleEditClick = () => {
    if (data?.isPredefined) {
      openPredefinedEditPage(salaryComponentId!);
    } else {
      openNonPayableBenefitsEditPage(salaryComponentId!);
    }
  };

  return (
    <>
      <Drawer isOpen={isOpen} onDismiss={onDismiss} zIndex={900}>
        <DrawerBody>
          <DetailsContainer onDismiss={onDismiss}>
            {isLoading && <DetailsViewSkeleton onDismiss={onDismiss} />}
            {isError && (
              <EmptyView
                description={
                  <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
                    Due to an unknown error, we could not load the component details page.
                  </Text>
                }>
                <Box display={'flex'} justifyContent={'center'}>
                  <Box width={'104px'}>
                    <Button icon={RefreshIcon} size={'small'} onClick={() => refetch()}>
                      Retry
                    </Button>
                  </Box>
                </Box>
              </EmptyView>
            )}
            {data ? (
              <>
                <DetailsHeader
                  heading={data.name}
                  description={data.description ?? ''}
                  isActive={Boolean(data.isActive)}
                  isPredefined={Boolean(data.isPredefined)}
                />

                <Divider marginY={'spacing.7'} />

                {/* General section */}
                <DetailsSection>
                  <Alert
                    color={'information'}
                    icon={UserIcon}
                    description={
                      <>
                        <Box display={'flex'} flexDirection={'row'} gap={'spacing.2'}>
                          <Text size={'medium'} color={'surface.text.gray.subtle'}>
                            This component is used by
                          </Text>
                          <Text weight={'medium'}>100 employees</Text>
                        </Box>
                        <Link
                          icon={ArrowRightIcon}
                          iconPosition={'right'}
                          size={'small'}
                          onClick={() => {
                            console.log('open salary structure list section');
                          }}>
                          View details
                        </Link>
                      </>
                    }
                    isDismissible={false}
                  />

                  <DetailsSectionHeading
                    icon={<CheckCircle2Icon size={'large'} color={'surface.icon.gray.subtle'} />}
                    label="General"
                  />
                  <DetailsSectionItemsContainer>
                    <DetailsSectionItem label="Component Name" value={data.name || ''} />
                    <DetailsSectionItem
                      label="Display name"
                      value={data.settings.behaviour.displayBehaviour?.displayName || ''}
                    />
                    <DetailsSectionItem
                      label="Part of salary structure"
                      value={nonPayableBenefitsDetails?.isPartOfCTC ? 'Yes' : 'No'}
                    />
                    <DetailsSectionItem
                      label="Employee visibility"
                      value={
                        nonPayableBenefitsDetails?.visibilityToEmployee
                          ? 'Visible in compensation'
                          : 'Not visible'
                      }
                    />
                  </DetailsSectionItemsContainer>
                </DetailsSection>
              </>
            ) : null}
          </DetailsContainer>
          {/* Actions */}
          <DetailsFooter isSticky={true}>
            <ModalActionButtons
              isActive={Boolean(data?.isActive)}
              showEnableDisableButton={!Boolean(data?.isPredefined)}
              openModal={openModal}
              onEditClick={handleEditClick}
            />
          </DetailsFooter>
        </DrawerBody>
      </Drawer>
      {data && (
        <ActionModals
          salaryComponent={data}
          currentActiveModal={currentActiveModal}
          closeModal={closeModal}
          enableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, it can be used in an employee's salary structure.{' '}
                </Text>
              </Box>
            </Box>
          }
          disableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once disabled, it cannot be used in an employee's salary structure.
                </Text>
              </Box>
            </Box>
          }
        />
      )}
    </>
  );
};

export default NonPayableBenefitsDetailView;
